import React, {
  type ComponentProps,
  Fragment,
  type ReactNode,
  type SVGProps,
  type ComponentType,
  type ComponentPropsWithoutRef,
  type ReactElement,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';

import { css } from 'glamor';

import { pushModal } from 'loot-core/client/actions';
import { reportBudget, rolloverBudget } from 'loot-core/client/queries';
import { type GroupType } from 'loot-core/shared/onboarding';
import { integerToCurrency } from 'loot-core/shared/util';
import {
  type CategoryEntity,
  type CategoryGroupEntity,
} from 'loot-core/src/types/models';

import { useCategories } from '../../hooks/useCategories';
import { useLocalPref } from '../../hooks/useLocalPref';
import { SvgSplit } from '../../icons/v0';
import { useResponsive } from '../../ResponsiveProvider';
import { type CSSProperties, theme } from '../../style';
import { gsGray300 } from '../../style/themes/light';
import { makeAmountFullStyle } from '../budget/util';
import { Text } from '../common/Text';
import { TextOneLine } from '../common/TextOneLine';
import { View } from '../common/View';
import { useSheetValue } from '../spreadsheet/useSheetValue';

import {
  Autocomplete,
  defaultFilterSuggestion,
  getItemName,
} from './Autocomplete';
import { ItemHeader } from './ItemHeader';

type CategoryAutocompleteItem = CategoryEntity & {
  group?: CategoryGroupEntity;
};

export type CategoryListProps = {
  items: (CategoryAutocompleteItem & {
    group?: CategoryGroupEntity;
    isNew?: boolean;
  })[];
  getItemProps?: (arg: {
    item: CategoryAutocompleteItem;
  }) => Partial<ComponentProps<typeof View>>;
  highlightedIndex: number;
  embedded?: boolean;
  footer?: ReactNode;
  renderSplitTransactionButton?: (
    props: ComponentPropsWithoutRef<typeof SplitTransactionButton>,
  ) => ReactElement<typeof SplitTransactionButton>;
  renderCategoryItemGroupHeader?: (
    props: ComponentPropsWithoutRef<typeof ItemHeader>,
  ) => ReactElement<typeof ItemHeader>;
  renderCategoryItem?: (
    props: ComponentPropsWithoutRef<typeof CategoryItem>,
  ) => ReactElement<typeof CategoryItem>;
  showHiddenItems?: boolean;
  showBalances?: boolean;
};
function CategoryList({
  items,
  getItemProps,
  highlightedIndex,
  embedded,
  footer,
  renderSplitTransactionButton = defaultRenderSplitTransactionButton,
  renderCategoryItemGroupHeader = defaultRenderCategoryItemGroupHeader,
  renderCategoryItem = defaultRenderCategoryItem,
  showHiddenItems,
  showBalances,
}: CategoryListProps) {
  let lastGroup: string | undefined | null = null;

  const filteredItems = useMemo(
    () =>
      showHiddenItems
        ? items
        : items.filter(item => !item.hidden && !item.group?.hidden),
    [showHiddenItems, items],
  );

  return (
    <View>
      <View
        style={{
          overflow: 'auto',
          padding: '5px 0',
          ...(!embedded && { maxHeight: 175 }),
        }}
      >
        {filteredItems.map((item, idx) => {
          if (item.id === 'split') {
            return renderSplitTransactionButton({
              key: 'split',
              ...(getItemProps ? getItemProps({ item }) : null),
              highlighted: highlightedIndex === idx,
              embedded,
            });
          }

          const showGroup = item.cat_group !== lastGroup;
          const groupName = `${item.group?.name}${item.group?.hidden ? ' (hidden)' : ''}`;
          lastGroup = item.cat_group;
          return (
            <Fragment key={item.id}>
              {showGroup && item.group?.name && (
                <Fragment key={item.group.name}>
                  {renderCategoryItemGroupHeader({
                    title: groupName,
                    style: {
                      ...(showHiddenItems &&
                        item.group?.hidden && { color: theme.pageTextSubdued }),
                    },
                  })}
                </Fragment>
              )}
              <Fragment key={item.id}>
                {renderCategoryItem({
                  ...(getItemProps ? getItemProps({ item }) : null),
                  item: {
                    ...item,
                    ...(item.isNew ? { name: `Create: '${item.name}'` } : {}),
                  },
                  highlighted: highlightedIndex === idx,
                  embedded,
                  style: {
                    ...(showHiddenItems &&
                      item.hidden && { color: theme.pageTextSubdued }),
                  },
                  showBalances,
                })}
              </Fragment>
            </Fragment>
          );
        })}
      </View>
      {footer}
    </View>
  );
}

type CategoryAutocompleteProps = ComponentProps<
  typeof Autocomplete<CategoryAutocompleteItem>
> & {
  categoryGroups?: Array<CategoryGroupEntity & { groupType: GroupType | null }>;
  showBalances?: boolean;
  showSplitOption?: boolean;
  allowCreateCategory?: boolean;
  creatingCategoryName?: string;
  onCreateNewCategory?: (
    newCatGroup: CategoryGroupEntity & { groupType: GroupType },
  ) => void;
  renderSplitTransactionButton?: (
    props: ComponentPropsWithoutRef<typeof SplitTransactionButton>,
  ) => ReactElement<typeof SplitTransactionButton>;
  renderCategoryItemGroupHeader?: (
    props: ComponentPropsWithoutRef<typeof ItemHeader>,
  ) => ReactElement<typeof ItemHeader>;
  renderCategoryItem?: (
    props: ComponentPropsWithoutRef<typeof CategoryItem>,
  ) => ReactElement<typeof CategoryItem>;
  showHiddenCategories?: boolean;
};

export function CategoryAutocomplete({
  categoryGroups,
  showBalances = true,
  showSplitOption,
  embedded,
  closeOnBlur,
  allowCreateCategory = false,
  creatingCategoryName,
  onCreateNewCategory,
  renderSplitTransactionButton,
  renderCategoryItemGroupHeader,
  renderCategoryItem,
  showHiddenCategories,
  ...props
}: CategoryAutocompleteProps) {
  const { grouped: defaultCategoryGroups = [] } = useCategories();
  const dispatch = useDispatch();
  const [categorySuggestions, setCategorySuggestions] = useState<
    CategoryAutocompleteItem[] | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndexFromDownshift, setHighlightedIndexFromDownshift] =
    useState(null);

  useEffect(() => {
    const catSuggestions = (categoryGroups || defaultCategoryGroups).reduce(
      (list, group) =>
        list.concat(
          (group.categories || [])
            .filter(category => category.cat_group === group.id)
            .map(category => ({
              ...category,
              group,
            })),
        ),
      showSplitOption ? [{ id: 'split', name: '' } as CategoryEntity] : [],
    );
    setCategorySuggestions(catSuggestions);
  }, [showSplitOption, defaultCategoryGroups, categoryGroups]);

  const closeCreateCategoryModalHandler = (isCreated: boolean) => {
    if (!isCreated) {
      const { value } = props;
      if (value != null) {
        const id = typeof value === 'string' ? value : value.id;
        const name = typeof value === 'string' ? value : value.name;
        props.onSelect?.(id, name);
      }
    }
    setCategorySuggestions(
      categorySuggestions.filter(c => !c.id.startsWith('add_category')),
    );
  };

  const createCategoryHandler = (category: string) => {
    setCategorySuggestions([
      ...categorySuggestions,
      {
        id: `add_category:${category}`,
        name: category,
        cat_group: 'New category',
        group: {
          name: null,
        },
      },
    ]);
    dispatch(
      pushModal('create-category', {
        categoryGroups,
        categoryName: category,
        onCreate: newGroup => {
          onCreateNewCategory(newGroup);
          props.onSelect?.(newGroup.name as any, newGroup.categories[0].name);
        },
        onDismiss: isCategoryCreated =>
          closeCreateCategoryModalHandler(isCategoryCreated),
      }),
    );
  };

  if (categorySuggestions === null) return null;

  // Extract and modify inputProps to handle keyboard navigation
  const { inputProps, ...autocompleteProps } = props;
  const modifiedInputProps = inputProps
    ? {
        ...inputProps,
        onBlur: e => {
          setIsOpen(false);
          inputProps.onBlur?.(e);
        },
        onFocus: e => {
          setIsOpen(true);
          inputProps.onFocus?.(e);
        },
        onChange: value => {
          setIsOpen(true);
          inputProps.onChange?.(value);
        },
        onKeyDown: e => {
          // Handle Shift+Enter - always let table navigate up
          if (e.key === 'Enter' && e.shiftKey) {
            return;
          }

          // Handle Enter (without shift)
          if (e.key === 'Enter' && !e.shiftKey) {
            // If dropdown is open and we have a highlighted item, select it
            if (isOpen && highlightedIndexFromDownshift !== null) {
              e.preventDefault();
              e.stopPropagation();
              // The Autocomplete component will handle the selection
              return;
            }
            // Otherwise, let it bubble to the table for navigation
            return;
          }

          // Stop propagation for arrow keys
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.stopPropagation();
          }

          inputProps?.onKeyDown?.(e);
        },
      }
    : undefined;

  return (
    <Autocomplete
      inputProps={modifiedInputProps}
      strict={true}
      highlightFirst={true}
      embedded={embedded}
      closeOnBlur={closeOnBlur}
      getHighlightedIndex={suggestions => {
        if (suggestions.length === 0) {
          return null;
        } else if (suggestions[0].id === 'split') {
          return suggestions.length > 1 ? 1 : null;
        }
        return 0;
      }}
      filterSuggestions={(suggestions, value) => {
        let hasExactMatch = false;
        const filteredSuggestions = suggestions.filter(suggestion => {
          if (getItemName(suggestion).toLowerCase() === value.toLowerCase()) {
            hasExactMatch = true;
          }
          return (
            suggestion.id === 'split' ||
            defaultFilterSuggestion(suggestion, value)
          );
        });
        if (allowCreateCategory && !hasExactMatch && value) {
          const suggestionsWithAdd = [
            {
              name: value,
              id: `add_category:${value}`,
              cat_group: 'New category',
              isNew: true,
            } as CategoryEntity,
          ].concat(filteredSuggestions);
          return suggestionsWithAdd;
        }

        return filteredSuggestions;
      }}
      suggestions={categorySuggestions}
      renderItems={(items, getItemProps, highlightedIndex) => (
        <CategoryList
          items={items}
          embedded={embedded}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          renderSplitTransactionButton={renderSplitTransactionButton}
          renderCategoryItemGroupHeader={renderCategoryItemGroupHeader}
          renderCategoryItem={renderCategoryItem}
          showHiddenItems={showHiddenCategories}
          showBalances={showBalances}
        />
      )}
      onHighlightedIndexChange={index =>
        setHighlightedIndexFromDownshift(index)
      }
      {...autocompleteProps}
      onSelect={(id, value) => {
        if (id && id.startsWith('add_category')) {
          // Clear the temporary category suggestions to prevent ghost entry
          // from appearing in the dropdown after creation
          setCategorySuggestions(
            categorySuggestions.filter(c => !c.id.startsWith('add_category')),
          );
          createCategoryHandler(value);
        } else {
          if (id === 'useMintCatName') {
            const existingCategory = categorySuggestions.find(
              c => c.name?.toLowerCase() === creatingCategoryName.toLowerCase(),
            );
            if (existingCategory) {
              props.onSelect?.(
                existingCategory.group?.name,
                existingCategory.name,
              );
            } else {
              props.onSelect?.(id, value);
              createCategoryHandler(creatingCategoryName);
            }
          } else {
            props.onSelect?.(id?.split('_')[0] ?? '', value);
          }
        }
      }}
    />
  );
}

function defaultRenderCategoryItemGroupHeader(
  props: ComponentPropsWithoutRef<typeof ItemHeader>,
): ReactElement<typeof ItemHeader> {
  return <ItemHeader {...props} type="category" />;
}

type SplitTransactionButtonProps = {
  Icon?: ComponentType<SVGProps<SVGElement>>;
  highlighted?: boolean;
  embedded?: boolean;
  style?: CSSProperties;
};

function SplitTransactionButton({
  Icon,
  highlighted,
  embedded,
  style,
  ...props
}: SplitTransactionButtonProps) {
  return (
    <View
      // Downshift calls `setTimeout(..., 250)` in the `onMouseMove`
      // event handler they set on this element. When this code runs
      // in WebKit on touch-enabled devices, taps on this element end
      // up not triggering the `onClick` event (and therefore delaying
      // response to user input) until after the `setTimeout` callback
      // finishes executing. This is caused by content observation code
      // that implements various strategies to prevent the user from
      // accidentally clicking content that changed as a result of code
      // run in the `onMouseMove` event.
      //
      // Long story short, we don't want any delay here between the user
      // tapping and the resulting action being performed. It turns out
      // there's some "fast path" logic that can be triggered in various
      // ways to force WebKit to bail on the content observation process.
      // One of those ways is setting `role="button"` (or a number of
      // other aria roles) on the element, which is what we're doing here.
      //
      // ref:
      // * https://github.com/WebKit/WebKit/blob/447d90b0c52b2951a69df78f06bb5e6b10262f4b/LayoutTests/fast/events/touch/ios/content-observation/400ms-hover-intent.html
      // * https://github.com/WebKit/WebKit/blob/58956cf59ba01267644b5e8fe766efa7aa6f0c5c/Source/WebCore/page/ios/ContentChangeObserver.cpp
      // * https://github.com/WebKit/WebKit/blob/58956cf59ba01267644b5e8fe766efa7aa6f0c5c/Source/WebKit/WebProcess/WebPage/ios/WebPageIOS.mm#L783
      role="button"
      style={{
        backgroundColor: highlighted
          ? theme.menuAutoCompleteBackgroundHover
          : 'transparent',
        borderRadius: embedded ? 4 : 0,
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 500,
        color: theme.noticeTextMenu,
        padding: '6px 8px',
        ':active': {
          backgroundColor: 'rgba(100, 100, 100, .25)',
        },
        ...style,
      }}
      data-testid="split-transaction-button"
      {...props}
    >
      <Text style={{ lineHeight: 0 }}>
        {Icon ? (
          <Icon style={{ marginRight: 5 }} />
        ) : (
          <SvgSplit width={10} height={10} style={{ marginRight: 5 }} />
        )}
      </Text>
      Split Transaction
    </View>
  );
}

function defaultRenderSplitTransactionButton(
  props: SplitTransactionButtonProps,
): ReactElement<typeof SplitTransactionButton> {
  return <SplitTransactionButton {...props} />;
}

type CategoryItemProps = {
  item: CategoryAutocompleteItem & {
    group?: CategoryGroupEntity;
    isNew?: boolean;
    disabled?: boolean;
  };
  className?: string;
  style?: CSSProperties;
  highlighted?: boolean;
  embedded?: boolean;
  showBalances?: boolean;
};

function CategoryItem({
  item,
  className,
  style,
  highlighted,
  embedded,
  showBalances,
  ...props
}: CategoryItemProps) {
  const { isNarrowWidth } = useResponsive();
  const [budgetType] = useLocalPref('budgetType');
  const [budgetId] = useLocalPref('id');

  let balance, isToBeBudgetedItem, toBudget;

  // nasty hack to avoid calling spreadsheet in mint import process
  // where the budget is not loaded, otherwise it fails with spreadsheet
  // undefined error
  if (budgetId) {
    // eslint-disable-next-line
    balance = useSheetValue(
      budgetType === 'rollover'
        ? rolloverBudget.catBalance(item.id)
        : reportBudget.catBalance(item.id),
    );

    isToBeBudgetedItem = item.id === 'to-be-budgeted';
    // eslint-disable-next-line
    toBudget = useSheetValue(rolloverBudget.toBudget);
  }

  return (
    <div
      style={style}
      // See comment above.
      role="button"
      className={`${className} ${css([
        {
          backgroundColor: highlighted
            ? theme.menuAutoCompleteBackgroundHover
            : 'transparent',
          color: highlighted
            ? theme.menuAutoCompleteItemTextHover
            : theme.menuAutoCompleteItemText,
          padding: 4,
          paddingLeft: 20,
          borderRadius: embedded ? 4 : 0,
          pointerEvents: item.disabled ? 'none' : 'auto',
          ...(item.isNew
            ? {
                color:
                  embedded && isNarrowWidth
                    ? theme.menuItemText
                    : theme.noticeTextMenu,
                borderRadius: embedded ? 4 : 0,
                fontSize: 11,
                fontWeight: 500,
              }
            : {}),
        },
      ])}`}
      data-testid={`${item.name}-category-item`}
      data-highlighted={highlighted || undefined}
      {...props}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextOneLine>
            {item.name}
            {item.hidden ? ' (hidden)' : null}
          </TextOneLine>
          {item.is_offbudget ? (
            <TextOneLine style={{ fontSize: 9, color: gsGray300 }}>
              (off budget)
            </TextOneLine>
          ) : null}
        </View>
        <TextOneLine
          style={{
            display: !showBalances ? 'none' : undefined,
            marginLeft: 5,
            flexShrink: 0,
            ...makeAmountFullStyle(isToBeBudgetedItem ? toBudget : balance, {
              positiveColor: theme.noticeTextMenu,
              negativeColor: theme.errorTextMenu,
            }),
          }}
        >
          {isToBeBudgetedItem
            ? toBudget != null
              ? ` ${integerToCurrency(toBudget || 0)}`
              : null
            : balance != null
              ? ` ${integerToCurrency(balance || 0)}`
              : null}
        </TextOneLine>
      </View>
    </div>
  );
}

function defaultRenderCategoryItem(
  props: ComponentPropsWithoutRef<typeof CategoryItem>,
): ReactElement<typeof CategoryItem> {
  return <CategoryItem {...props} />;
}
